/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useEffect } from "react";

export const useEventListener = (
  eventType: string,
  handler: EventListenerOrEventListenerObject,
  targetOrTargetGetter:
    | EventTarget
    | null
    | (() => EventTarget | null) = document
): void => {
  useEffect(() => {
    const target: EventTarget | null =
      targetOrTargetGetter instanceof Function
        ? targetOrTargetGetter()
        : targetOrTargetGetter;

    target?.addEventListener(eventType, handler);
    return (): void => {
      target?.removeEventListener(eventType, handler);
    };
  }, [eventType, handler, targetOrTargetGetter]);
};

export const useNonTextEventListener = (
  eventType: string,
  handler: EventListener,
  target = document
): void => {
  const nonTextHandler = useCallback(
    (event) => {
      if (
        ["INPUT", "TEXTAREA", "SP-TEXTFIELD"].includes(
          event.target.tagName.toUpperCase()
        )
      ) {
        return;
      }

      return handler(event);
    },
    [handler]
  );

  useEventListener(eventType, nonTextHandler, target);
};
